/**
 * Auth + team gate. Cloud mode: Supabase email/password or magic link, then
 * pick/create/join a team. Local mode: a fixed local identity, no screens.
 *
 * Email + password and magic link ONLY for v1 — the moment any third-party
 * login is offered, Apple requires Sign in with Apple too (plan §2.1).
 */
import { kvGet, kvSet } from '@coach/core';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { APP } from '../app.config';
import { AppMark } from './brand';
import { AppProvider, LOCAL_TEAM, LOCAL_USER, type Identity, type TeamInfo } from './app';
import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { AUTH_LANDING, authRedirect, getPendingJoin, parseConfirmLink, parseJoinLink, setPendingJoin, supabase, type LinkType } from './supabase';
import { Turnstile, type TurnstileHandle } from './turnstile';
import { LegalLinks } from './legal';

type User = { id: string; email: string; name: string };
type Phase =
  | { kind: 'loading' }
  | { kind: 'signin'; msg?: string }
  | { kind: 'newpassword'; user: User }
  | { kind: 'teams'; user: User; teams: TeamInfo[] }
  | { kind: 'ready'; identity: Identity };

/** Supabase's wording → something a coach can act on. */
export function friendlyAuthError(raw: string): string {
  const m = raw.toLowerCase();
  if (m.includes('invalid login credentials'))
    return "That email and password don't match. If you've only ever signed in with an email link, you don't have a password yet — tap \"Forgot password?\" to set one.";
  if (m.includes('email not confirmed')) return 'Confirm your email first — open the link we sent when you created the account (or use "Email me a sign-in link").';
  if (m.includes('expired') || m.includes('invalid or has expired'))
    return 'That email link or code has expired or was already used. Only the newest email works — request a new one below.';
  if (m.includes('captcha')) return "The security check didn't go through. Try again.";
  if (m.includes('rate limit') || m.includes('security purposes') || m.includes('too many'))
    return 'Too many tries in a short time. Wait a minute, then try again.';
  if (m.includes('same') && m.includes('password')) return 'Pick a password different from your old one.';
  return raw;
}

/** accept_invite's errors → what the invited coach should do. */
export function friendlyInviteError(raw: string): string {
  const m = raw.toLowerCase();
  if (m.includes('not found')) return "That code doesn't match an invite. Check it against the email, or ask your head coach for a new one.";
  if (m.includes('already used')) return 'That invite was already used. If it wasn’t you, ask your head coach for a new one.';
  if (m.includes('expired')) return 'That invite has expired. Ask your head coach to send a new one.';
  return raw;
}

export function AuthGate({ children }: { children: ReactNode }) {
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  // Arrived through a password-reset email (or code): once signed in, ask for
  // the new password before anything else.
  const recovering = useRef(AUTH_LANDING.recovery);
  const landingError = useRef<string | null>(AUTH_LANDING.error);
  // Arrived through an emailed /auth/confirm link (web), not yet verified.
  const landingConfirm = useRef(AUTH_LANDING.confirm);

  const loadTeams = useCallback(async (user: User) => {
    const { data, error } = await supabase!
      .from('memberships')
      .select('role, team_id, teams(id, name, season)')
      .eq('user_id', user.id);
    if (error) {
      setPhase({ kind: 'signin', msg: error.message });
      return;
    }
    const teams: TeamInfo[] = (data ?? []).map((m) => {
      const t = (Array.isArray(m.teams) ? m.teams[0] : m.teams) as { id: string; name: string; season: string };
      return { id: t.id, name: t.name, season: t.season, role: m.role as TeamInfo['role'] };
    });
    const last = await kvGet<string>('currentTeam');
    const pick = teams.find((t) => t.id === last) ?? (teams.length === 1 ? teams[0] : undefined);
    // Arrived through an invite link: show the join screen first, even for a
    // coach who already has a team.
    if (pick && !getPendingJoin()) setPhase({ kind: 'ready', identity: { userId: user.id, userName: user.name, email: user.email, team: pick } });
    else setPhase({ kind: 'teams', user, teams });
  }, []);

  const apply = useCallback(async () => {
    const { data } = await supabase!.auth.getSession();
    const u = data.session?.user;
    if (!u) {
      const err = landingError.current;
      landingError.current = null;
      setPhase({ kind: 'signin', msg: err ? friendlyAuthError(err) : undefined });
      return;
    }
    const user = { id: u.id, email: u.email ?? '', name: (u.user_metadata?.['display_name'] as string) || u.email?.split('@')[0] || 'Coach' };
    if (recovering.current) {
      setPhase({ kind: 'newpassword', user });
      return;
    }
    await loadTeams(user);
  }, [loadTeams]);

  useEffect(() => {
    if (!supabase) {
      setPhase({
        kind: 'ready',
        identity: { userId: LOCAL_USER, userName: 'Coach', email: '', team: { id: LOCAL_TEAM, name: 'My Team', season: '', role: 'head' } },
      });
      return;
    }
    // An emailed link carries a single-use token_hash: trade it for a session.
    // Success fires SIGNED_IN (→ apply); failure shows Supabase's reason.
    const confirmLink = async (link: { tokenHash: string; type: LinkType }) => {
      if (link.type === 'recovery') recovering.current = true;
      const { error } = await supabase!.auth.verifyOtp({ token_hash: link.tokenHash, type: link.type });
      if (error) {
        landingError.current = error.message;
        await apply();
      }
    };
    const first = landingConfirm.current;
    landingConfirm.current = null;
    if (first) void confirmLink(first);
    else void apply();

    // Native app: an emailed readybreak.app link opens the app (universal
    // link) instead of Safari. Capacitor holds a cold-start URL until this
    // listener attaches, so a link that launched the app is not lost.
    let removeUrlListener: (() => void) | undefined;
    let closed = false;
    if (Capacitor.isNativePlatform()) {
      void CapApp.addListener('appUrlOpen', ({ url }) => {
        const join = parseJoinLink(url);
        if (join) {
          setPendingJoin(join);
          setPhase({ kind: 'loading' });
          void apply();
          return;
        }
        const link = parseConfirmLink(url);
        if (!link) return;
        setPhase({ kind: 'loading' });
        void confirmLink(link);
      }).then((h) => {
        if (closed) void h.remove();
        else removeUrlListener = () => void h.remove();
      });
    }
    const { data: sub } = supabase.auth.onAuthStateChange((evt) => {
      if (evt === 'PASSWORD_RECOVERY') recovering.current = true;
      if (evt === 'SIGNED_IN' || evt === 'SIGNED_OUT' || evt === 'USER_UPDATED' || evt === 'PASSWORD_RECOVERY') {
        // Never await Supabase calls inside this callback (it holds the auth lock).
        setTimeout(() => void apply(), 0);
      }
    });
    return () => {
      closed = true;
      removeUrlListener?.();
      sub.subscription.unsubscribe();
    };
  }, [apply]);

  const onAuthRequired = useCallback(() => setPhase({ kind: 'signin', msg: 'Your session expired — sign in to keep syncing.' }), []);
  const onSignOut = useCallback(async () => {
    await supabase?.auth.signOut();
    setPhase({ kind: 'signin' });
  }, []);
  const onSwitchTeam = useCallback(async (teamId: string) => {
    await kvSet('currentTeam', teamId);
    if (phase.kind === 'ready') {
      const { data } = await supabase!.auth.getUser();
      const u = data.user;
      if (u) await loadTeams({ id: u.id, email: u.email ?? '', name: phase.identity.userName });
    }
  }, [phase, loadTeams]);

  if (phase.kind === 'loading') return <div className="spinner" />;
  if (phase.kind === 'signin') return <SignIn msg={phase.msg} />;
  if (phase.kind === 'newpassword')
    return (
      <NewPassword
        user={phase.user}
        onDone={() => {
          recovering.current = false;
          void loadTeams(phase.user);
        }}
      />
    );
  if (phase.kind === 'teams')
    return (
      <TeamPicker
        user={phase.user}
        teams={phase.teams}
        onPick={async (t) => {
          await kvSet('currentTeam', t.id);
          setPhase({ kind: 'ready', identity: { userId: phase.user.id, userName: phase.user.name, email: phase.user.email, team: t } });
        }}
        onChanged={() => loadTeams(phase.user)}
      />
    );
  return (
    <AppProvider identity={phase.identity} onAuthRequired={onAuthRequired} onSignOut={onSignOut} onSwitchTeam={onSwitchTeam}>
      {children}
    </AppProvider>
  );
}

/**
 * The old football-planner.pages.dev address still serves the app (Cloudflare
 * Pages always does), but it is not an allowed Turnstile hostname, so nobody
 * can sign in there. Anyone who reaches the sign-in screen on it is sent to
 * the real address. Coaches already signed in on the old address are left
 * alone — their session and any unsynced local changes live on that origin.
 * Preview deployments (<hash>.football-planner.pages.dev) are not redirected.
 */
const CANONICAL_ORIGIN = 'https://readybreak.app';
const RETIRED_HOST = 'football-planner.pages.dev';

function SignIn({ msg }: { msg?: string }) {
  if (typeof location !== 'undefined' && location.hostname === RETIRED_HOST) return <MovedToCanonical />;
  return <SignInForm msg={msg} />;
}

/** App mark + name at the top of every signed-out screen. */
function BrandHeader() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
      <AppMark size={56} className="app-mark-lg" />
      <h1 style={{ color: 'var(--gold)', fontSize: 26, margin: 0 }}>{APP.name}</h1>
    </div>
  );
}

function MovedToCanonical() {
  useEffect(() => {
    const t = setTimeout(() => location.replace(CANONICAL_ORIGIN + location.pathname + location.search + location.hash), 1500);
    return () => clearTimeout(t);
  }, []);
  return (
    <div style={{ maxWidth: 420, margin: '10vh auto', padding: 16 }}>
      <BrandHeader />
      <p style={{ marginBottom: 16 }}>{APP.name} has moved to <strong>readybreak.app</strong>. Taking you there…</p>
      <a className="btn btn-primary btn-block" href={CANONICAL_ORIGIN}>Go to readybreak.app</a>
      <p className="muted small" style={{ marginTop: 12 }}>If you added the old address to your home screen, remove it and add readybreak.app instead.</p>
    </div>
  );
}

type Mode = 'signin' | 'signup' | 'magic' | 'reset' | 'code';

function SignInForm({ msg }: { msg?: string }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const invited = !!getPendingJoin();
  const [mode, setMode] = useState<Mode>(invited ? 'signup' : 'signin');
  // Which email the code screen is confirming: a sign-in link or a password reset.
  const [codeFor, setCodeFor] = useState<'email' | 'recovery'>('email');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(msg ?? null);
  const captcha = useRef<TurnstileHandle>(null);

  const switchTo = (m: Mode) => {
    setMode(m);
    setNote(null);
  };

  const go = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setNote(null);
    try {
      const sb = supabase!;
      if (mode === 'code') {
        // Confirming a code is not captcha-gated.
        const { error } = await sb.auth.verifyOtp({ email, token: code.replace(/\s+/g, ''), type: codeFor });
        if (error) throw error;
        return; // the auth listener takes it from here
      }
      // Every other call is gated: earn a fresh single-use token right now.
      const token = await captcha.current?.getToken();
      const withCaptcha = token ? { captchaToken: token } : {};
      if (mode === 'signup') {
        const { error } = await sb.auth.signUp({ email, password, options: { data: { display_name: name }, emailRedirectTo: authRedirect(), ...withCaptcha } });
        if (error) throw error;
        setNote('Check your email to confirm your account, then sign in.');
        setMode('signin');
      } else if (mode === 'magic') {
        const { error } = await sb.auth.signInWithOtp({ email, options: { emailRedirectTo: authRedirect(), ...withCaptcha } });
        if (error) throw error;
        setCodeFor('email');
        setCode('');
        setMode('code');
      } else if (mode === 'reset') {
        const { error } = await sb.auth.resetPasswordForEmail(email, { redirectTo: authRedirect(), ...withCaptcha });
        if (error) throw error;
        setCodeFor('recovery');
        setCode('');
        setMode('code');
      } else {
        const { error } = await sb.auth.signInWithPassword({ email, password, options: { ...withCaptcha } });
        if (error) throw error;
      }
    } catch (err) {
      setNote(friendlyAuthError((err as Error).message));
    } finally {
      setBusy(false);
    }
  };

  const label =
    mode === 'signup' ? 'Create account' : mode === 'magic' ? 'Email me a sign-in link' : mode === 'reset' ? 'Email me a reset link' : mode === 'code' ? 'Continue' : 'Sign in';

  return (
    <div style={{ maxWidth: 420, margin: '10vh auto', padding: 16 }}>
      <BrandHeader />
      {invited ? (
        <p className="card" style={{ margin: '0 0 16px' }}>
          <strong>You've been invited to join a team.</strong>{' '}
          {mode === 'signin' ? 'Sign in' : 'Create an account (or sign in if you have one)'} and you'll join it right after.
        </p>
      ) : (
        <p className="muted" style={{ marginBottom: 20 }}>{APP.description}</p>
      )}
      <form onSubmit={go} className="card" style={{ margin: 0 }}>
        {mode === 'code' ? (
          <>
            <p style={{ marginTop: 0 }}>
              We emailed <strong>{email}</strong>. Tap the link in that email, or type the code from it here.
            </p>
            <p className="muted small">{Capacitor.isNativePlatform() ? 'If the link opens in Safari instead of the app, type the code here.' : 'On an iPhone home-screen app, use the code: email links open in Safari, not in the app.'}</p>
            <div className="field">
              <label>Code from the email</label>
              <input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code" pattern="[0-9 ]{6,12}" required />
            </div>
          </>
        ) : (
          <>
            {mode === 'signup' && (
              <div className="field">
                <label>Your name</label>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Coach Mike" required />
              </div>
            )}
            {mode === 'reset' && <p className="muted small" style={{ marginTop: 0 }}>We'll email you a link and a code to set a new password.</p>}
            <div className="field">
              <label>Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
            </div>
            {(mode === 'signin' || mode === 'signup') && (
              <div className="field">
                <label>Password</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} autoComplete={mode === 'signup' ? 'new-password' : 'current-password'} />
              </div>
            )}
          </>
        )}
        <Turnstile ref={captcha} />
        {note && <p style={{ color: 'var(--gold-light)', fontSize: 13, margin: '6px 0 10px' }}>{note}</p>}
        <button className="btn btn-primary btn-block" disabled={busy}>
          {busy ? 'One moment…' : label}
        </button>
        {mode === 'signin' && (
          <div style={{ textAlign: 'center', marginTop: 10 }}>
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => switchTo('reset')}>Forgot password?</button>
          </div>
        )}
        <div className="row" style={{ justifyContent: 'space-between', marginTop: 12, fontSize: 13 }}>
          {mode === 'code' ? (
            <>
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => switchTo(codeFor === 'recovery' ? 'reset' : 'magic')}>Send a new email</button>
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => switchTo('signin')}>Back to sign in</button>
            </>
          ) : (
            <>
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => switchTo(mode === 'signup' ? 'signin' : 'signup')}>
                {mode === 'signup' ? 'Have an account? Sign in' : 'New here? Create account'}
              </button>
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => switchTo(mode === 'magic' || mode === 'reset' ? 'signin' : 'magic')}>
                {mode === 'magic' || mode === 'reset' ? 'Use a password' : 'Use a magic link'}
              </button>
            </>
          )}
        </div>
        <LegalLinks style={{ marginTop: 14 }} />
      </form>
    </div>
  );
}

/** After a password-reset link or code: signed in, now set the new password. */
function NewPassword({ user, onDone }: { user: User; onDone: () => void }) {
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pw !== pw2) {
      setNote("The two passwords don't match.");
      return;
    }
    setBusy(true);
    setNote(null);
    const { error } = await supabase!.auth.updateUser({ password: pw });
    setBusy(false);
    if (error) setNote(friendlyAuthError(error.message));
    else onDone();
  };

  return (
    <div style={{ maxWidth: 420, margin: '10vh auto', padding: 16 }}>
      <BrandHeader />
      <p className="muted" style={{ marginBottom: 20 }}>Set a new password for {user.email}.</p>
      <form onSubmit={save} className="card" style={{ margin: 0 }}>
        <div className="field">
          <label>New password</label>
          <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={8} autoComplete="new-password" />
        </div>
        <div className="field">
          <label>Type it again</label>
          <input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required minLength={8} autoComplete="new-password" />
        </div>
        {note && <p style={{ color: 'var(--gold-light)', fontSize: 13, margin: '6px 0 10px' }}>{note}</p>}
        <button className="btn btn-primary btn-block" disabled={busy}>{busy ? 'Saving…' : 'Save password'}</button>
        <div style={{ textAlign: 'center', marginTop: 10 }}>
          <button type="button" className="btn btn-sm btn-ghost" onClick={onDone}>Skip for now</button>
        </div>
      </form>
    </div>
  );
}

function TeamPicker({ user, teams, onPick, onChanged }: { user: { id: string; email: string; name: string }; teams: TeamInfo[]; onPick: (t: TeamInfo) => void; onChanged: () => void }) {
  const [teamName, setTeamName] = useState('');
  const [season, setSeason] = useState(String(new Date().getFullYear()));
  const [invite] = useState(getPendingJoin);
  const [token, setToken] = useState(invite ?? '');
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const create = async () => {
    setBusy(true);
    setNote(null);
    const { error } = await supabase!.rpc('create_team_with_org', { team_name: teamName, season });
    setBusy(false);
    if (error) setNote(error.message);
    else onChanged();
  };
  const join = async () => {
    setBusy(true);
    setNote(null);
    const { data, error } = await supabase!.rpc('accept_invite', { invite_token: token.trim().toUpperCase() });
    setBusy(false);
    if (error) {
      setNote(friendlyInviteError(error.message));
      return;
    }
    setPendingJoin(null);
    if (typeof data === 'string') await kvSet('currentTeam', data);
    onChanged();
  };
  const notNow = () => {
    setPendingJoin(null);
    onChanged();
  };

  return (
    <div style={{ maxWidth: 480, margin: '6vh auto', padding: 16 }}>
      <h1 style={{ color: 'var(--gold)', fontSize: 22 }}>Welcome, {user.name}</h1>
      {invite && (
        <>
          <div className="section-title">You've been invited</div>
          <div className="card" style={{ margin: '0 12px' }}>
            <p style={{ marginTop: 0 }}>Your head coach invited you to their team. Tap Join to get their plans, playbook and roster.</p>
            <div className="field"><label>Invite code</label><input value={token} onChange={(e) => setToken(e.target.value)} /></div>
            <button className="btn btn-green btn-block" disabled={busy || !token.trim()} onClick={join}>{busy ? 'Joining…' : 'Join team'}</button>
            {note && <p style={{ color: 'var(--gold-light)', fontSize: 13, margin: '10px 0 0' }}>{note}</p>}
            <div style={{ textAlign: 'center', marginTop: 8 }}>
              <button className="btn btn-sm btn-ghost" onClick={notNow}>Not now</button>
            </div>
          </div>
        </>
      )}
      {!invite && teams.length > 0 && (
        <>
          <div className="section-title">Your teams</div>
          <div className="list">
            {teams.map((t) => (
              <button key={t.id} className="list-item" style={{ width: '100%', border: 'none', cursor: 'pointer', textAlign: 'left' }} onClick={() => onPick(t)}>
                <div className="grow">
                  <div>{t.name}</div>
                  <div className="muted small">{t.season} · {t.role}</div>
                </div>
                <span>›</span>
              </button>
            ))}
          </div>
        </>
      )}
      {!invite && (<>
      <div className="section-title">Start a new team</div>
      <div className="card" style={{ margin: '0 12px' }}>
        <div className="field"><label>Team name</label><input value={teamName} onChange={(e) => setTeamName(e.target.value)} placeholder="e.g. Eagles 7th Grade" /></div>
        <div className="field"><label>Season</label><input value={season} onChange={(e) => setSeason(e.target.value)} /></div>
        <button className="btn btn-primary btn-block" disabled={busy || !teamName.trim()} onClick={create}>Create team — you'll be head coach</button>
      </div>
      <div className="section-title">Join with an invite code</div>
      <div className="card" style={{ margin: '0 12px' }}>
        <div className="field"><label>Invite code</label><input value={token} onChange={(e) => setToken(e.target.value)} placeholder="paste the code from your head coach" /></div>
        <button className="btn btn-green btn-block" disabled={busy || !token.trim()} onClick={join}>Join team</button>
      </div>
      {note && <p style={{ color: 'var(--gold-light)', fontSize: 13, margin: 12 }}>{note}</p>}
      </>)}
      <div style={{ textAlign: 'center', marginTop: 16 }}>
        <button className="btn btn-sm btn-ghost" onClick={() => supabase!.auth.signOut()}>Sign out ({user.email})</button>
      </div>
    </div>
  );
}
